import {
  addDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  limit,
} from 'firebase/firestore';
import { type Order, type RecurringOrder, type RecurringOrderLine, type RecurringOrderStatus } from '../types';
import { initFirebase } from '../lib/firebase';
import { recurringOrdersCollection, recurringOrderDoc, ordersCollection, orderDoc } from '../lib/firestore';
import { reportError } from '../lib/error-reporting';

/**
 * Create a recurring order template from scratch.
 */
export async function createRecurringOrder(input: {
  tenantId: string;
  marketId: string;
  customerName: string;
  phoneNumber?: string;
  preferredPickupTime: string;
  lines: RecurringOrderLine[];
  note?: string;
}): Promise<RecurringOrder> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;

  const nowISO = new Date().toISOString();
  const newRecurring: RecurringOrder = {
    id: '',
    tenantId: input.tenantId,
    marketId: input.marketId,
    customerName: input.customerName.trim(),
    phoneNumber: input.phoneNumber?.trim() || undefined,
    preferredPickupTime: input.preferredPickupTime,
    lines: input.lines,
    status: 'active',
    note: input.note?.trim() || undefined,
    createdAtISO: nowISO,
    updatedAtISO: nowISO,
  };

  const docRef = await addDoc(recurringOrdersCollection(db), newRecurring);
  return { ...newRecurring, id: docRef.id };
}

/**
 * Create a recurring order template from an existing order.
 * Also marks the original order as recurring by setting its recurringOrderId.
 */
export async function createRecurringOrderFromOrder(order: Order): Promise<RecurringOrder> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;

  if (!order.tenantId) throw new Error('tenantId requis sur la commande');
  if (!order.marketId) throw new Error('marketId requis sur la commande');

  // Extract HH:mm from the pickup ISO timestamp
  const pickupDate = new Date(order.pickupAtISO);
  const hours = String(pickupDate.getHours()).padStart(2, '0');
  const minutes = String(pickupDate.getMinutes()).padStart(2, '0');
  const preferredPickupTime = `${hours}:${minutes}`;

  const lines: RecurringOrderLine[] = order.lines.map((l) => ({
    productId: l.productId,
    quantity: l.quantity,
    options: l.options && l.options.length > 0 ? l.options : undefined,
    portionSizeId: l.portionSizeId,
  }));

  const nowISO = new Date().toISOString();
  const newRecurring: RecurringOrder = {
    id: '',
    tenantId: order.tenantId,
    marketId: order.marketId,
    customerName: order.customerName,
    phoneNumber: order.phoneNumber,
    preferredPickupTime,
    lines,
    status: 'active',
    note: order.note,
    createdAtISO: nowISO,
    updatedAtISO: nowISO,
    lastGeneratedDate: order.pickupDate,
  };

  const docRef = await addDoc(recurringOrdersCollection(db), newRecurring);
  const recurringOrderId = docRef.id;

  // Link the original order to this recurring template
  await updateDoc(orderDoc(db, order.id), {
    recurringOrderId,
    updatedAt: serverTimestamp(),
  });

  return { ...newRecurring, id: recurringOrderId };
}

/**
 * Get all recurring orders for a tenant (one-time fetch).
 */
export async function getRecurringOrders(tenantId: string): Promise<RecurringOrder[]> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;

  const q = query(
    recurringOrdersCollection(db),
    where('tenantId', '==', tenantId),
    orderBy('customerName', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data());
}

/**
 * Subscribe to real-time updates for all recurring orders of a tenant.
 */
export function onRecurringOrdersSnapshot(
  tenantId: string,
  callback: (orders: RecurringOrder[]) => void,
): () => void {
  const { db, currentUserPromise } = initFirebase();

  let unsubscribe: (() => void) | null = null;
  let isActive = true;

  currentUserPromise
    .then(() => {
      if (!isActive) return;

      const q = query(
        recurringOrdersCollection(db),
        where('tenantId', '==', tenantId),
        orderBy('customerName', 'asc'),
      );

      unsubscribe = onSnapshot(
        q,
        (snap) => {
          callback(snap.docs.map((d) => d.data()));
        },
        (error) => {
          reportError(error, { context: 'onRecurringOrdersSnapshot', tenantId });
          callback([]);
        },
      );
    })
    .catch((error) => {
      reportError(error, { context: 'onRecurringOrdersSnapshot.auth', tenantId });
      callback([]);
    });

  return () => {
    isActive = false;
    if (unsubscribe) unsubscribe();
  };
}

/**
 * Update a recurring order template.
 * Also updates the most recent future pending order generated from this template.
 */
export async function updateRecurringOrder(
  id: string,
  updates: Partial<Pick<RecurringOrder, 'customerName' | 'phoneNumber' | 'preferredPickupTime' | 'lines' | 'note'>>,
): Promise<void> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;

  const ref = recurringOrderDoc(db, id);
  await updateDoc(ref, {
    ...updates,
    updatedAt: serverTimestamp(),
  });

  // Also update the next pending order generated from this template (if any)
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const todayKey = `${year}-${month}-${day}`;

  const q = query(
    ordersCollection(db),
    where('recurringOrderId', '==', id),
    where('pickupDate', '>=', todayKey),
    where('status', 'in', ['pending', 'awaiting_validation']),
    orderBy('pickupDate', 'asc'),
    limit(1),
  );
  const snap = await getDocs(q);

  if (!snap.empty) {
    const nextOrderRef = orderDoc(db, snap.docs[0].id);
    const orderUpdates: Record<string, unknown> = { updatedAt: serverTimestamp() };
    if (updates.customerName !== undefined) orderUpdates.customerName = updates.customerName;
    if (updates.phoneNumber !== undefined) orderUpdates.phoneNumber = updates.phoneNumber ?? null;
    if (updates.note !== undefined) orderUpdates.note = updates.note ?? null;
    if (updates.lines !== undefined) {
      orderUpdates.lines = updates.lines.map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        preparedQuantity: 0,
        options: l.options ?? null,
        portionSizeId: l.portionSizeId ?? null,
      }));
    }
    if (updates.preferredPickupTime !== undefined) {
      // Rebuild pickupAtISO with the new time on the existing pickupDate
      const nextOrder = snap.docs[0].data();
      const [h, m] = updates.preferredPickupTime.split(':').map(Number);
      const [yr, mo, d2] = nextOrder.pickupDate.split('-').map(Number);
      const newPickupAt = new Date(yr, mo - 1, d2, h, m, 0, 0);
      orderUpdates.pickupAtISO = newPickupAt.toISOString();
    }
    await updateDoc(nextOrderRef, orderUpdates);
  }
}

/**
 * Set the status of a recurring order (pause or resume).
 */
export async function setRecurringOrderStatus(id: string, status: RecurringOrderStatus): Promise<void> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;

  await updateDoc(recurringOrderDoc(db, id), {
    status,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a recurring order template permanently.
 * Does NOT delete already-generated orders.
 */
export async function deleteRecurringOrder(id: string): Promise<void> {
  const { db, auth, currentUserPromise } = initFirebase();
  if (!auth.currentUser) await currentUserPromise;

  await deleteDoc(recurringOrderDoc(db, id));
}
